// Workspace navigation rendered as a collapsible desktop sidebar and mobile sheet.

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronRight, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useBranding } from '@/app-config/runtime/use-branding';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from '@/components/ui/sidebar';
import { WorkspaceAccountMenu } from './WorkspaceAccountMenu';
import {
  useWorkspaceNavigation,
  type WorkspaceNavigationGroup,
} from './use-workspace-navigation';

export function AppSidebar({
  workspaceName,
  collapsible = 'icon',
}: {
  workspaceName: string;
  collapsible?: 'icon' | 'offcanvas';
}) {
  const { t } = useTranslation('shell');
  const branding = useBranding();
  const { tenantId, featured, overview, groups, label, isActive } =
    useWorkspaceNavigation();

  return (
    <Sidebar collapsible={collapsible}>
      <SidebarHeader className="p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              render={<Link to="/w/$tenantId" params={{ tenantId }} />}
              title={t('overview')}
            >
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-background text-foreground shadow-sm">
                <Sparkles className="size-4" aria-hidden="true" />
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {branding.shortName}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {workspaceName}
                </span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent className="gap-1 py-2">
        {overview || featured ? (
          <SidebarGroup className="py-1">
            <SidebarMenu>
              {overview ? (
                <NavigationItem
                  item={overview}
                  tenantId={tenantId}
                  label={label(overview)}
                  active={isActive(overview)}
                />
              ) : null}
              {featured ? (
                <NavigationItem
                  item={featured}
                  tenantId={tenantId}
                  label={label(featured)}
                  active={isActive(featured)}
                />
              ) : null}
            </SidebarMenu>
          </SidebarGroup>
        ) : null}

        {groups.map((group) => (
          <NavGroupSection
            key={group.key}
            group={group}
            tenantId={tenantId}
            label={label}
            isActive={isActive}
          />
        ))}
      </SidebarContent>

      <SidebarFooter className="p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <WorkspaceAccountMenu presentation="sidebar" />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

type NavItemModel =
  ReturnType<typeof useWorkspaceNavigation>['groups'][number]['items'][number];

function NavGroupSection({
  group,
  tenantId,
  label,
  isActive,
}: {
  group: WorkspaceNavigationGroup;
  tenantId: string;
  label: (item: NavItemModel) => string;
  isActive: (item: NavItemModel) => boolean;
}) {
  // 可折叠分组（教务设置）默认收起；若当前正位于该分组下的页面则展开，
  // 以免用户看不到自己所处位置。
  const [open, setOpen] = useState(group.active);
  const menu = (
    <SidebarMenu>
      {group.items.map((item) => (
        <NavigationItem
          key={item.routeId}
          item={item}
          tenantId={tenantId}
          label={label(item)}
          active={isActive(item)}
        />
      ))}
    </SidebarMenu>
  );

  if (!group.collapsible) {
    return (
      <SidebarGroup className="py-1">
        <SidebarGroupLabel className="h-7 px-2 text-2xs font-medium tracking-wide">
          {group.label}
        </SidebarGroupLabel>
        {menu}
      </SidebarGroup>
    );
  }

  return (
    <SidebarGroup className="py-1">
      <SidebarGroupLabel
        className="h-7 cursor-pointer justify-between px-2 text-2xs font-medium tracking-wide"
        render={
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
          />
        }
      >
        <span>{group.label}</span>
        <ChevronRight
          aria-hidden="true"
          className={cn('size-3.5 transition-transform', open && 'rotate-90')}
        />
      </SidebarGroupLabel>
      {open ? menu : null}
    </SidebarGroup>
  );
}

function NavigationItem({
  item,
  tenantId,
  label,
  active,
}: {
  item: ReturnType<
    typeof useWorkspaceNavigation
  >['groups'][number]['items'][number];
  tenantId: string;
  label: string;
  active: boolean;
}) {
  const Icon = item.icon;
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        tooltip={label}
        isActive={active}
        render={<Link to={item.to.replace('$tenantId', tenantId) as never} />}
      >
        <Icon aria-hidden="true" />
        <span>{label}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}
